/**
 * Contractul unei acțiuni de business.
 *
 * O acțiune e definită O SINGURĂ DATĂ și devine simultan:
 *   - server action apelabilă din UI,
 *   - endpoint în API-ul public (/api/actions),
 *   - unealtă apelabilă de un agent AI (schema JSON se derivă din Zod),
 *   - intrare în AuditLog (automat, nu opțional).
 *
 * Regula: dacă o operație schimbă starea companiei, ea TREBUIE să fie o acțiune.
 * Nu scrie mutații direct în componente sau în route handlers.
 */
import type { z } from "zod";
import type { VisEntity } from "@/lib/visibility";

/** Cine execută acțiunea și în numele cărei companii. */
export type ActionCtx = {
  /** Firma în care rulează acțiunea. La acțiunile de platformă e firma-țintă (sau
   *  PLATFORM_SCOPE la provizionare, când firma încă nu există). */
  companyId: string;
  actor: { id?: string; name: string };
  source: "UI" | "AI" | "API" | "SYSTEM" | "AUTOMATION" | "PLATFORM";
  /**
   * Operatorul instalarii (`User.superAdmin`). Se pune DOAR din sesiune (`lib/actions/ui.ts`),
   * niciodata dintr-o cheie API: cheile coboara la rolul incorporat ADMIN, care nu e superadmin.
   *
   * ⚠ Nu-l deduce din `perms` — cheia unui rol configurat e aleasa de client, deci un rol numit
   * „superadmin" ar trece drept unul adevarat. Dreptul se citeste de pe cont, nu dintr-un nume.
   */
  superAdmin?: boolean;
  /**
   * SYSTEM = apelant server-side de încredere (înregistrare self-service, vitrina publică).
   * PLATFORM = adminul rodesk.io, prin cheia de platformă. Cele două planuri sunt DISJUNCTE:
   * PLATFORM nu poate atinge date de business, iar rolurile de firmă nu pot administra
   * platforma. Vezi `roleRefusal`.
   */
  role?: "ADMIN" | "HR" | "MANAGER" | "OPERATOR" | "SYSTEM" | "PLATFORM";
  /** Rolul CONFIGURAT al apartenenței (null/absent = incorporat) — ținta regulilor de partajare. */
  roleId?: string | null;
  /**
   * Setul de permisiuni al apelantului — sursa REALĂ a drepturilor de când rolurile sunt
   * configurabile. Lipsa lui nu e o portiță: gardul cade atunci pe politica rolului
   * incorporat din `role`, adică pe exact comportamentul de dinainte.
   */
  perms?: import("@/lib/roles").PermissionSet;
  /**
   * Acțiunea-PĂRINTE care a chemat sub-acțiunea asta (`retail.sell → invoice.create`).
   *
   * ⛔ Se pune DOAR din cod, cu `caDinActiune()` — niciodată dintr-o cerere HTTP: e cheia care
   * spune „dreptul a fost verificat pe părinte". O acțiune-orchestrator își cheamă piesele cu
   * drepturile EI, nu ale omului: altfel rolul de tejghea (care are voie să vândă) era refuzat
   * la `invoice.create`, fiindcă factura ține de modulul „facturare".
   */
  viaAction?: string;
  /**
   * Domeniile permise CHEII API cu care a intrat cererea (`ApiKey.scopes`), când cererea vine
   * prin `/api/actions`. Gol/absent = fără îngustare (sesiune de om, cron, apel intern).
   *
   * Poarta pe scope se închide în route handler, pe modulul ACȚIUNII. Nu e de ajuns pentru
   * acțiunile care aleg din intrare ce entitate ating (`sync.pull`): acolo modulul acțiunii e
   * „integrari”, dar datele scoase sunt ale altui modul, deci scope-ul trebuie verificat a
   * doua oară, pe modulul entității cerute. De aceea lista călătorește până în handler.
   */
  scopes?: string[];
  /**
   * Cheia de idempotență dată de apelantul extern (header `Idempotency-Key`). Registrul o
   * folosește ca a doua trimitere a ACELEIAȘI cereri să întoarcă rezultatul primei, în loc
   * să producă un al doilea document. Se pune DOAR din poarta HTTP.
   */
  idempotencyKey?: string;
};

/** companyId sentinelă pentru acțiunile de platformă care nu au încă o firmă-țintă. */
export const PLATFORM_SCOPE = "__platform__";

/** Ce s-a întâmplat, pe înțelesul unui om (și al unui LLM care citește istoricul). */
export type AuditInfo = { entityId: string; summary: string };

/**
 * Domeniile în care se împart acțiunile — după ele se croiesc agenții specializați ȘI
 * drepturile rolurilor (RBAC). Listă ca VALOARE, nu doar ca tip: ecranul de roluri o
 * parcurge ca să afișeze modulele, iar o a doua listă scrisă de mână acolo ar rămâne în
 * urmă la primul modul nou — cu drepturi care există în cod și lipsesc din interfață.
 */
export const ACTION_MODULES = [
  "crm", "stocuri", "depozite", "productie", "vanzari", "achizitii",
  "magazin", "retail", "facturare", "trezorerie", "integrari", "documente", "comunicare", "sistem",
  "proiecte", "hr", "marketing", "rapoarte", "contracte",
  /* Module desprinse pe 02.10.2026 din cele mari, ca administratorul să le poată acorda separat.
     Acțiunile mutate poartă `origine` (modulul de unde au venit) — vezi `ActionDef.origine`. */
  "licitatii", "catalog", "politica",
  /** planul de control al ecosistemului — administrarea firmelor, venită din adminul .io */
  "platforma",
] as const;

export type ActionModule = (typeof ACTION_MODULES)[number];

/** Etichete RO pentru ecranul de roluri. „platforma” nu apare: e planul de control. */
export const MODULE_LABEL: Record<ActionModule, string> = {
  crm: "CRM (clienți, lead-uri, oportunități)",
  vanzari: "Vânzări (oferte, comenzi)",
  facturare: "Facturare (facturi, e-Factura, e-Transport)",
  trezorerie: "Trezorerie (încasări, plăți, bancă)",
  stocuri: "Produse & stocuri",
  depozite: "Depozite / WMS",
  productie: "Producție (rețetare, comenzi, raportare)",
  achizitii: "Achiziții (furnizori, recepții, cheltuieli)",
  magazin: "Magazin online",
  retail: "Magazine fizice",
  proiecte: "Proiecte & pontaj",
  hr: "Resurse umane",
  marketing: "Marketing & campanii",
  contracte: "Contracte & abonamente",
  documente: "Documente",
  comunicare: "Comunicare & calendar",
  rapoarte: "Rapoarte & analiză (rapoarte, BI, pivot, semnale)",
  integrari: "Integrări (chei, webhooks, SAGA, ANAF)",
  sistem: "Sistem (setări, roluri, asistent AI)",
  licitatii: "Licitații (SEAP/SICAP, dosare, CPV)",
  catalog: "Catalog & Magazine online (atribute, categorii, imagini, Magento)",
  politica: "Politică comercială (grile, încadrare, prețuri minime)",
  platforma: "Platformă (plan de control)",
};

/**
 * Aceleași module, în două cuvinte — pentru locurile unde eticheta e o pastilă, un antet sau o
 * coloană: consola de operațiuni, jurnalul de activitate. `Record<ActionModule, …>` e voit:
 * un modul nou nu compilează până nu-și primește și forma scurtă. Hărțile scrise de mână prin
 * ecrane rămâneau în urmă — jurnalul de audit eticheta „Sistem" tot ce era producție, HR,
 * proiecte, marketing, contracte sau rapoarte, fiindcă lipseau din lista lui locală.
 */
export const MODULE_SHORT: Record<ActionModule, string> = {
  crm: "CRM",
  vanzari: "Vânzări",
  facturare: "Facturare",
  trezorerie: "Trezorerie",
  stocuri: "Produse & stocuri",
  depozite: "Depozite",
  productie: "Producție",
  achizitii: "Achiziții",
  magazin: "Magazin online",
  retail: "Magazine",
  proiecte: "Proiecte",
  hr: "Resurse umane",
  marketing: "Marketing",
  contracte: "Contracte",
  documente: "Documente",
  comunicare: "Comunicare",
  rapoarte: "Rapoarte & analiză",
  integrari: "Integrări",
  sistem: "Sistem",
  licitatii: "Licitații",
  catalog: "Catalog",
  politica: "Politică comercială",
  platforma: "Platformă",
};

/** Modulele care se pot acorda unui rol de firmă — „platforma” e alt plan, nu un drept. */
export const ASSIGNABLE_MODULES = ACTION_MODULES.filter((m) => m !== "platforma");

/** Vezi `ActionDef.cheiCost`. */
export type CheiCost = { chei: readonly string[]; regula?: "marfa" | "orar" };

/** Vezi `ActionDef.ascunde`. Cheia = nodul din arbore (`field:Entitate.camp` sau `page:/produse#sectiune`). */
export type AscundePeNod = Readonly<Record<string, readonly string[]>>;

export type ActionDef<I = unknown, O = unknown> = {
  /** identificator stabil, ex. "stock.move" — folosit de AI și de API */
  key: string;
  /** eticheta din UI (RO) */
  title: string;
  /** descriere orientată spre AI: CÂND se folosește, nu doar ce face */
  description: string;
  module: ActionModule;
  /**
   * Module CONSUMATOARE suplimentare, doar pentru citiri partajate (typeahead-uri, căutări):
   * apelantul trece dacă poate citi modulul principal SAU oricare de aici. Motivul: WMS-ul,
   * producția și recepțiile caută articole prin `search.products` (modul stocuri) — un rol
   * configurat doar cu `depozite` ar trece garda rutei, dar n-ar putea selecta niciun produs,
   * deci nu ar putea crea nicio sarcină. Nu pune pe mutații: scrierea rămâne pe UN modul.
   */
  modules?: ActionModule[];
  /**
   * Căutare/alegere DIN DOCUMENT: acțiunea întoarce înregistrări ale modulului ei ca să fie
   * alese pe un document al altui modul (clientul pe factură, produsul pe comandă). Trece și
   * cu nivelul „Vizualizare” (VIEW) pe `module` — omul nu vede modulul, dar îi poate folosi
   * datele în documentele lui. Doar pe citiri; o mutație cu `lookup` e refuzată de probă.
   */
  lookup?: boolean;
  /**
   * Modulul din care a fost MUTATĂ acțiunea la desprinderea modulelor noi (02.10.2026). NU mai e
   * gardă de drepturi (din v2.47.1 modulul nou se acordă separat) — rămâne doar pentru cheile API
   * emise pe modulul vechi (`scopeAllows`) și pentru pachetul vândut (`packageOfAction`).
   */
  origine?: ActionModule;
  /**
   * Module pe care mutația le cere în PLUS față de `module`, tot cu drept de scriere. Livrarea
   * comenzii (`order.fulfill`, `delivery.create`) e în Vânzări, dar scoate marfa din gestiune;
   * facturarea din comandă e în Vânzări, dar emite un document fiscal. Un agent de vânzări cu
   * Stocuri / Facturare doar la citire nu trebuie să poată face nici una, chiar dacă scrie în
   * Vânzări (verificarea în browser ca agent, 24.09.2026). Excepția explicită pe acțiune bate.
   */
  requiresAlso?: ActionModule[];
  /** entitatea atinsă — devine entityType în AuditLog */
  entity: string;
  /**
   * VIZIBILITATEA PE RÂND, verificată de registru înainte de handler. Firma poate ține CRM-ul
   * pe `owner`/`team`; citirile respectă asta prin `visibilityWhere`, scrierile trebuie s-o
   * respecte la fel. Acțiunea declară ce entitate atinge și din ce chei de intrare vin id-urile;
   * registrul refuză — ca DATE, cu audit — dacă vreunul e în afara a ce vede apelantul.
   */
  visibility?: {
    entity: VisEntity | ((input: I) => VisEntity);
    idKeys: string[];
    when?: (input: I) => boolean;
  };
  /**
   * ENTITĂȚILE SCRISE în afara etichetei acțiunii, când au politică de câmp. Registrul aplică
   * pentru fiecare aceleași două gărzi ca pentru `entity`: dreptul de modificare pe pagina ei
   * și refuzul scrierilor pe câmpurile interzise.
   */
  writesEntities?: string[] | ((input: I) => string[]);
  /** false = doar citește/analizează (un agent o poate rula fără aprobare) */
  mutates: boolean;
  /**
   * Scrierea NU golește memoria agregatelor firmei (`agregate-cache.ts`). Pentru acțiunile de
   * ceas și de sistem (cursul BNR, bătaia cozii de joburi, numărători), care rulează de zeci de
   * ori pe oră și nu schimbă cifrele grilelor: fiecare golire făcea următoarea intrare pe
   * /achizitii, /vanzari, /stocuri să recalculeze tot (9 s pe producție, 02.10.2026). Ce scriu
   * joburile în bază apare oricum la expirarea termenului, ca la orice import.
   */
  pastreazaAgregate?: boolean;
  /** operație riscantă: un agent AI ar trebui să ceară confirmare umană */
  confirm?: boolean;
  /**
   * Acțiunea DISTRUGE ceva: șterge un rând, stornează un document, anulează o comandă.
   * Cere dreptul de ȘTERGERE din arborele de acces, nu doar `WRITE` pe modul — până acum
   * cine putea îndrepta o factură o putea și storna, fiindcă erau același drept.
   *
   * Nemarcat, se DEDUCE din cheia acțiunii (`*.delete`, `*.remove`, `*.void`, `*.cancel`,
   * `*.revoke`, `*Delete`, `*Remove`, `*Cancel`…) — vezi `isDestructive` din registry.ts.
   * Deducerea acoperă cele ~78 de acțiuni existente fără să le atingem pe rând; steagul e
   * pentru cele care nu se numesc după convenție, în ambele sensuri (`false` scoate din regulă
   * o acțiune al cărei nume înșală, ca `count.cancel` dacă ar fi doar o schimbare de stare).
   */
  destructive?: boolean;
  /**
   * Cheile din REZULTAT (la orice adâncime: `linii[]`, `randuri[]`, hărți pe id) care poartă un
   * cost — golite de registru pentru apelantul care nu are voie să-l vadă. Pentru costul cuibărit,
   * pe care mascarea pe entitate (un singur nivel, ambalaje standard) nu-l atinge: liniile de
   * recepție, retururile, transferurile, marja pe sucursală. `regula`: „marfa” (implicit) = urmează
   * `Product.purchasePrice`; „orar” = tariful orar intern al oamenilor (`ProjectMemberRate.costRate`).
   * Cheile se aleg pe acțiune, nu global: `valoare` e cost într-o recepție și preț într-o factură.
   */
  cheiCost?: CheiCost;
  /**
   * Datele CUIBĂRITE ale rezultatului, legate de nodul din arbore care le decide (22.10.2026). Mascarea pe entitate
   * atinge doar primul nivel (`maskResult`), iar citirile fișei produsului își grupează datele adânc (`lista.pret`,
   * `receptii[]`, `fisa.clasaContare`) — bifele de câmp sau de secțiune din Setări › Roluri nu le atingeau. Pentru
   * fiecare nod pe care apelantul NU are „vede”, cheile date se golesc la orice adâncime: listele devin `[]`,
   * restul `null`, iar rezultatul primește `_ascunse` (nodurile închise), ca fila să spună „ascuns pentru rolul
   * tău”, nu „nimic”. Cheile se aleg pe acțiune, ca la `cheiCost`: `pret` e preț de listă aici și altceva în altă parte.
   */
  ascunde?: AscundePeNod;
  /**
   * Mutație asupra PROPRIEI memorii a agentului, nu asupra stării companiei (memory.*).
   * Nu cere aprobare nici în modul supravegheat — altfel agentul ar întrerupe fiecare
   * discuție ca să ceară voie să-și ia notițe. Rămâne auditată, vizibilă în /memorie
   * și reversibilă (memory.forget). Nu marca așa nimic ce atinge date de business.
   */
  noApproval?: boolean;
  /** doar ADMIN (administrare utilizatori etc.) */
  adminOnly?: boolean;
  /**
   * Acțiune asupra PROPRIULUI cont al apelantului (profil, parolă, 2FA, sesiuni).
   * Ocolește politica pe module din `roles.ts` — și trebuie să o ocolească: modulul
   * ei e „sistem", la care rolul HR n-are drept de scriere, deci fără steagul ăsta
   * un om de la personal n-ar putea să-și schimbe propria parolă.
   *
   * Contractul pe care se sprijină scutirea: handler-ul are voie să atingă EXCLUSIV
   * rândul lui `ctx.actor.id`. Nu marca așa nimic ce primește un id din intrare.
   */
  selfService?: boolean;
  /**
   * Schimbă DATELE DE ACCES ale apelantului (parolă, 2FA, profil, sesiunile altor dispozitive).
   * Pe un cont partajat (`User.credentialsLocked`) registrul o refuză înainte de handler —
   * un singur loc, nu câte o verificare în fiecare acțiune.
   */
  credentialChange?: boolean;
  /**
   * Acțiune de PLAN DE CONTROL: administrează firme, nu date dintr-o firmă. O poate rula
   * exclusiv cheia de platformă (rolul PLATFORM), niciodată un utilizator sau o cheie de
   * firmă — și invers, cheia de platformă nu poate rula nimic altceva.
   */
  platformOnly?: boolean;
  input: z.ZodType<I>;
  /**
   * Fotografia entității înainte și după execuție. Dacă e definită, registrul o apelează
   * de două ori și calculează diff-ul → istoricul de versiuni vine gratis.
   */
  snapshot?: (input: I, ctx: ActionCtx) => Promise<Record<string, unknown> | null>;
  /**
   * „Trecerea asta n-a făcut nimic." Când întoarce `true`, execuția reușită NU se mai scrie în
   * jurnal — dar numai dacă vine din cron sau dintr-un apelant de sistem; o apăsare de om se
   * consemnează întotdeauna, chiar dacă n-a schimbat nimic, fiindcă „am apăsat și n-a mers
   * nimic" e exact întrebarea la care jurnalul trebuie să răspundă.
   *
   * ⛔ Zece treceri de cron × fiecare firmă × la fiecare 5 minute au însemnat 281.246 din
   * 332.039 de rânduri (84,7 %) și 150 MB de tabelă, aproape toate spunând „n-am găsit nimic
   * de făcut". Urma reală a firmei se îneca în ele.
   *
   * Se declară pe fiecare acțiune în parte, cu ce înseamnă „gol" ACOLO — nu se ghicește din
   * forma rezultatului.
   */
  idleWhen?: (result: O) => boolean;
  handler: (input: I, ctx: ActionCtx) => Promise<O>;
  /**
   * Rezumatul lizibil al a ceea ce s-a întâmplat. Poate fi asincron: rezolvă id-urile în
   * SKU-uri și coduri de locație — aceste rezumate devin memoria pe termen lung a agenților,
   * deci nu lăsa id-uri opace în ele.
   * `before` = fotografia dinainte (dacă acțiunea definește snapshot).
   */
  audit: (input: I, result: O, before?: Record<string, unknown> | null) => AuditInfo | Promise<AuditInfo>;
};

/** Eroare de business, cu mesaj afișabil utilizatorului (spre deosebire de un crash). */
export class ActionError extends Error {
  constructor(message: string, readonly fields?: Record<string, string>) {
    super(message);
    this.name = "ActionError";
  }
}

/**
 * REFUZUL ÎNTORS CA DATE.
 *
 * Rezultatele previzibile (limită de credit depășită, stare greșită, cod nepotrivit) se ÎNTORC,
 * nu se aruncă: în producție Next maschează mesajul unui `throw` dintr-o server action. Prețul e
 * că `ActionResult.ok === true` nu mai înseamnă „s-a făcut" — înseamnă doar „a rulat".
 *
 * ⛔ De aici încolo, orice apelant citește refuzul cu `esteRefuz` / `motivulRefuzului`, nu cu
 * adevărul unui obiect nevid. Un `groupTransfer.accept` care n-a citit refuzul marca transferul
 * ACCEPTAT fără comandă, iar ecranul magazinului anunța „Factura a fost emisă" pentru o factură
 * refuzată.
 */
export type RefuzDeDate = { ok: false; motiv?: string; motivCredit?: string; [k: string]: unknown };

/**
 * `true` dacă datele întoarse de o acțiune sunt un REFUZ, nu un rezultat.
 *
 * ⛔ Nu e de ajuns `ok === false`. Există acțiuni care întorc `ok:false` ca DATĂ obișnuită —
 * o verificare care răspunde „nu", o consultare care spune „n-am găsit". Refuzul adevărat vine
 * întotdeauna cu MOTIVUL lui: fără el, n-ar avea ce arăta omului. Cu regula largă, trei
 * scenarii de probă au picat cu „Operațiunea a fost refuzată" pe rezultate perfect valide.
 */
export function esteRefuz(data: unknown): data is RefuzDeDate {
  if (!data || typeof data !== "object" || Array.isArray(data)) return false;
  const d = data as { ok?: unknown; motiv?: unknown; motivCredit?: unknown };
  return d.ok === false && (typeof d.motiv === "string" || typeof d.motivCredit === "string");
}

/**
 * FORMA UNICĂ a unui refuz întors ca date. Se folosește la orice refuz nou din orice handler.
 *
 * ⛔ De ce există: `esteRefuz` cere `motiv`, iar zeci de handlere întorceau doar `message` —
 * deci registrul le dădea apelantului ca REUȘITĂ, cu refuzul îngropat în date. Un document
 * care nu s-a creat raporta succes prin API și prin unealta agentului AI, ecranele se
 * revalidau, automatizările porneau, iar `emitEvent` anunța aplicațiile terțe. Măsurat pe
 * 05.09.2026: 105 asemenea locuri în 29 de fișiere din catalog.
 *
 * `message` se pune la fel cu `motiv`, deliberat: ecranele scrise înainte îl citeau de acolo,
 * iar un refuz nu are voie să-și schimbe textul văzut de om doar pentru că s-a mutat câmpul.
 *
 *     if (l.status !== "DRAFT") return refuz(`${l.number} nu mai e ciornă.`, { number: l.number });
 *
 * ⛔ NU e generică, deliberat. Varianta `refuz<T extends object>(…): {…} & T` se instanțiază o
 * dată pe fiecare apel — la 97 de apeluri a împins proiectul peste bugetul de instanțieri al
 * TypeScript, iar TS a renunțat tăcut la tipurile unor `include`-uri din fișiere fără nicio
 * legătură (`supplier-funds`, `supplier-pricing`, `work-queue`, `stock-engine`). Vezi memoria
 * `buget-tipuri-typescript`. Câmpurile suplimentare rămân `unknown` la citire — nu contează:
 * handlerele stau în `ActionDef<any, any>[]`, iar callback-urile de `audit` își declară singure
 * tipul parametrului.
 */
export type Refuz = { ok: false; motiv: string; message: string; [k: string]: unknown };
export function refuz(motiv: string, restul?: Record<string, unknown>): Refuz {
  return { ok: false as const, motiv, message: motiv, ...(restul ?? {}) };
}

/** Motivul refuzului, oricare ar fi forma lui. Gol dacă nu e un refuz. */
export function motivulRefuzului(data: unknown): string {
  if (!esteRefuz(data)) return "";
  return String(data.motiv ?? data.motivCredit ?? "Operațiunea a fost refuzată.");
}
